<script setup lang="ts">
/**
 * One tile of an `NxChoiceTileGroup`: a media area (the `media` slot — an
 * icon, a thumbnail, a schematic preview), the `title`, an optional
 * `description` and `badge`, and a check mark while selected. The whole tile
 * is the control (radio or checkbox, after the group); `disabled` tiles stay
 * visible but cannot be picked. The default slot adds content under the text.
 */
import Check from 'lucide-vue-next/dist/esm/icons/check.js';
import { computed, inject, onBeforeUnmount } from 'vue';
import { choiceTileContextKey, type NxChoiceValue } from './context';

const props = withDefaults(
  defineProps<{
    value: NxChoiceValue;
    title: string;
    description?: string;
    badge?: string;
    disabled?: boolean;
  }>(),
  { description: undefined, badge: undefined, disabled: false }
);

const group = inject(choiceTileContextKey, null);
if (!group) {
  throw new Error('NxChoiceTile must be used inside NxChoiceTileGroup.');
}
const unregister = group.register(props.value, () => props.disabled);
onBeforeUnmount(unregister);

const selected = computed(() => group.isSelected(props.value));
const role = computed(() => (group.multiple.value ? 'checkbox' : 'radio'));
const tabindex = computed(() => (props.disabled || !group.isTabStop(props.value) ? -1 : 0));

function pick() {
  if (props.disabled) return;
  if (!group!.multiple.value && selected.value) return;
  group!.toggle(props.value);
}

function onKeydown(event: KeyboardEvent) {
  if (event.key !== ' ' && event.key !== 'Enter') return;
  event.preventDefault();
  pick();
}
</script>

<template>
  <div
    class="nx-choice-tile"
    :class="{ 'is-selected': selected, 'is-disabled': props.disabled }"
    :role="role"
    :aria-checked="selected"
    :aria-disabled="props.disabled || undefined"
    :tabindex="tabindex"
    data-choice-tile
    data-testid="nx-choice-tile"
    @click="pick"
    @keydown="onKeydown"
  >
    <div v-if="$slots.media" class="nx-choice-tile__media" aria-hidden="true">
      <slot name="media" />
    </div>
    <div class="nx-choice-tile__text">
      <span class="nx-choice-tile__title-row">
        <strong class="nx-choice-tile__title">{{ props.title }}</strong>
        <span v-if="props.badge" class="nx-choice-tile__badge">{{ props.badge }}</span>
      </span>
      <span v-if="props.description" class="nx-choice-tile__description">
        {{ props.description }}
      </span>
      <slot />
    </div>
    <span class="nx-choice-tile__check" aria-hidden="true">
      <Check class="nxt-icon-xs" />
    </span>
  </div>
</template>

<style scoped>
.nx-choice-tile {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-sm);
  min-width: 0;
  padding: var(--nxt-space-sm);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-lg);
  background: var(--nxt-surface-raised-bg);
  box-shadow: var(--nxt-shadow-sm);
  cursor: pointer;
  outline: none;
  transition:
    border-color var(--nxt-motion-fast) var(--nxt-ease-out),
    box-shadow var(--nxt-motion-fast) var(--nxt-ease-out),
    transform var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-choice-tile:hover:not(.is-disabled) {
  border-color: var(--nxt-border-strong);
  box-shadow: var(--nxt-shadow-md);
  transform: translateY(calc(var(--nxt-hairline) * -1));
}
.nx-choice-tile:focus-visible {
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-choice-tile.is-selected {
  border-color: var(--nxt-brand-border);
  background: color-mix(in srgb, var(--nxt-brand-subtle-bg) 60%, var(--nxt-surface-raised-bg));
  box-shadow:
    0 0 0 var(--nxt-hairline) var(--nxt-brand-border),
    var(--nxt-shadow-sm);
}
.nx-choice-tile.is-disabled {
  cursor: not-allowed;
  opacity: 0.55;
}
.nx-choice-tile__media {
  display: grid;
  place-items: center;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-sunken-bg);
  color: var(--nxt-fg-subtle);
}
.nx-choice-tile__text {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  min-width: 0;
  padding-inline-end: var(--nxt-space-lg);
}
.nx-choice-tile__media + .nx-choice-tile__text {
  padding-inline-end: 0;
}
.nx-choice-tile__title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-2xs) var(--nxt-space-xs);
}
.nx-choice-tile__title {
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-choice-tile__badge {
  padding: 0 var(--nxt-space-xs);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-neutral-subtle-bg);
  color: var(--nxt-neutral-subtle-fg);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  line-height: 1.6;
}
.nx-choice-tile__description {
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  line-height: var(--nxt-line-height-normal);
}
.nx-choice-tile__check {
  position: absolute;
  inset-block-start: var(--nxt-space-sm);
  inset-inline-end: var(--nxt-space-sm);
  display: grid;
  place-items: center;
  width: calc(var(--nxt-size-unit) * 1.25);
  height: calc(var(--nxt-size-unit) * 1.25);
  border: var(--nxt-hairline) solid var(--nxt-border-strong);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-surface-raised-bg);
  color: transparent;
  transition:
    background-color var(--nxt-motion-fast) var(--nxt-ease-out),
    color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-choice-tile.is-selected .nx-choice-tile__check {
  border-color: var(--nxt-brand-bg);
  background: var(--nxt-brand-bg);
  color: var(--nxt-brand-fg);
}
@media (prefers-reduced-motion: reduce) {
  .nx-choice-tile,
  .nx-choice-tile__check {
    transition: none;
  }
  .nx-choice-tile:hover:not(.is-disabled) {
    transform: none;
  }
}
/* The same where a subtree asks for less motion: data-nx-motion="reduced"
   on an ancestor (reset.css does the durations). */
:where([data-nx-motion='reduced']) .nx-choice-tile,
:where([data-nx-motion='reduced']) .nx-choice-tile__check {
  transition: none;
}
:where([data-nx-motion='reduced']) .nx-choice-tile:hover:not(.is-disabled) {
  transform: none;
}

@media (forced-colors: active) {
  .nx-choice-tile.is-selected {
    outline: calc(var(--nxt-hairline) * 2) solid Highlight;
  }
}
</style>
